import { useEffect, useState } from 'react';
import {
  Modal,
  Tabs,
  Text,
  Group,
  Badge,
  SimpleGrid,
  Paper,
  ThemeIcon,
  Divider,
  Stack,
  Timeline,
  LoadingOverlay,
  ScrollArea,
  Blockquote,
  Box,
  Title,
  Button,
  Select, 
  TextInput
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconCar,
  IconFileCertificate,
  IconHistory,
  IconGasStation,
  IconTool,
  IconAlertCircle,
  IconCheck,
  IconBuildingStore,
  IconDeviceDesktopAnalytics,
  IconInfoCircle,
  IconArchive,
  IconCalendarEvent
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import api from '../../api/axiosConfig';

export default function VehicleDetailModal({ opened, close, vehicle, onVehicleArchived }) {
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Arşiv Modalı State'leri
  const [archiveModalOpened, { open: openArchiveModal, close: closeArchiveModal }] = useDisclosure(false);
  const [archiveReason, setArchiveReason] = useState('Satıldı');
  const [customReason, setCustomReason] = useState('');
  const [isArchiving, setIsArchiving] = useState(false);

  useEffect(() => {
    if (vehicle && opened) {
      fetchHistory();
      setArchiveReason('Satıldı'); 
    }
  }, [vehicle, opened]);

  const fetchHistory = async () => {
    if (!vehicle?.id) return;
    setLoadingHistory(true);
    try {
      const response = await api.get(`/vehicles/maintenance/vehicle/${vehicle.id}`);
      setHistory(response.data || []);
    } catch (error) {
      console.error('Geçmiş yüklenirken hata:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleArchiveVehicle = async () => {
    const finalReason = archiveReason === 'Diğer' ? customReason.trim() : archiveReason;

    if (archiveReason === 'Diğer' && !finalReason) {
      alert('Lütfen arşivleme sebebini belirtin.');
      return;
    }

    setIsArchiving(true);
    try {
      await api.put(`/vehicles/${vehicle.id}/archive`, {
        reason: finalReason,
      });
      
      closeArchiveModal();
      setCustomReason(''); 
      close(); 
      
      if (onVehicleArchived) {
        onVehicleArchived();
      }
    } catch (error) {
      console.error('Arşivleme sırasında hata oluştu:', error);
      alert('Arşivleme işlemi başarısız oldu.');
    } finally {
      setIsArchiving(false);
    }
  };

  if (!vehicle) return null;

  const formatDate = (date) => (date ? dayjs(date).format('DD.MM.YYYY') : '-');
  // Arşiv tarihi formatı (saati de gösterebilirsin istersen)
  const formatArchiveDate = (date) => (date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '-');

  const formatCurrency = (val) =>
    val?.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' }) || '0 ₺';

  const InfoRow = ({ label, value }) => (
    <div>
      <Text c="dimmed" size="xs" fw={600}>{label}</Text>
      <Text fw={600} size="sm">{value || '-'}</Text>
    </div>
  );

  const getTimelineStyle = (type) => {
    const safeType = type || '';
    if (safeType.includes('Arıza')) return { color: 'red', icon: <IconAlertCircle size={14} /> };
    if (safeType.includes('Periyodik')) return { color: 'blue', icon: <IconCheck size={14} /> };
    if (safeType.includes('Lastik')) return { color: 'orange', icon: <IconCar size={14} /> };
    return { color: 'gray', icon: <IconTool size={14} /> };
  };

  const StatusBadge = ({ label, active, detail }) => (
    <Paper
      withBorder
      p="sm"
      radius="lg"
      bg={active ? 'blue.0' : 'gray.0'}
      style={{
        borderColor: active ? 'var(--mantine-color-blue-2)' : 'var(--mantine-color-gray-2)',
      }}
    >
      <Group justify="space-between" align="flex-start">
        <div>
          <Text size="sm" fw={700} c={active ? 'blue.8' : 'dark'}>{label}</Text>
          {active && detail && <Text size="xs" mt={4} c="dimmed">No: {detail}</Text>}
        </div>
        <Badge size="sm" radius="xl" color={active ? 'blue' : 'gray'} variant={active ? 'filled' : 'light'}>
          {active ? 'Var' : 'Yok'}
        </Badge>
      </Group>
    </Paper>
  );

  // Arşiv sebebi rengi belirleyici
  const getReasonColor = (reason) => {
    if (!reason) return 'gray';
    if (reason === 'Satıldı') return 'teal';
    if (reason === 'Hurdaya Ayrıldı') return 'red';
    if (reason === 'Kiralama Süresi Bitti') return 'orange';
    return 'violet';
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={close}
        centered
        size="xl"
        radius="xl"
        overlayProps={{ blur: 3 }}
        title={
          <Group gap="sm">
            <ThemeIcon size={38} radius="xl" variant="light" color={vehicle.is_archived ? "gray" : "blue"}>
              {vehicle.is_archived ? <IconArchive size={20} /> : <IconCar size={20} />}
            </ThemeIcon>
            <Text fw={800}>{vehicle.is_archived ? 'Arşivli Araç Kartı' : 'Araç Detay Kartı'}</Text>
          </Group>
        }
      >
        <Stack gap="lg">
          {/* Üst hero */}
          <Paper
            radius="xl"
            p="lg"
            style={{
              position: 'relative',
              overflow: 'hidden',
              // Eğer arşivliyse rengi gri/siyah tonlarına çekiyoruz
              background: vehicle.is_archived 
                ? 'linear-gradient(135deg, rgba(30,41,59,0.98) 0%, rgba(15,23,42,0.95) 100%)'
                : 'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.95) 45%, rgba(59,130,246,0.90) 100%)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <Box
              style={{
                position: 'absolute',
                top: -40,
                right: -40,
                width: 120,
                height: 120,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.07)',
              }}
            />

            <Group justify="space-between" align="flex-start" wrap="wrap" style={{ position: 'relative', zIndex: 1 }}>
              <Group wrap="nowrap" align="center">
                <ThemeIcon
                  size={56}
                  radius="xl"
                  variant="white"
                  color="rgba(255,255,255,0.12)"
                >
                  {vehicle.is_archived ? <IconArchive size={28} color="white" /> : <IconCar size={28} color="black" />}
                </ThemeIcon>

                <div>
                  <Title order={3} c="white" fw={900}>
                    {vehicle.plate}
                  </Title>
                  <Text size="sm" c="rgba(255,255,255,0.76)" fw={500}>
                    {vehicle.brand} - {vehicle.commercial_type || 'Belirtilmemiş'}
                  </Text>
                </div>
              </Group>

              {/* Sağ Üst Aksiyonlar / Bilgiler */}
              <Group>
                <Badge size="lg" radius="xl" color="green" variant="light">
                  {vehicle.ownership || '-'}
                </Badge>

                {/* ARAÇ AKTİF İSE: ARŞİVE KALDIR BUTONUNU GÖSTER */}
                {!vehicle.is_archived && (
                  <Button 
                    color="orange" 
                    variant="light" 
                    radius="xl" 
                    size="sm"
                    leftSection={<IconArchive size={16} />}
                    onClick={openArchiveModal}
                  >
                    Arşive Kaldır
                  </Button>
                )}
              </Group>
            </Group>

            {/* ARAÇ ARŞİVLİ İSE: ARŞİV BİLGİLERİ ŞERİDİ */}
            {vehicle.is_archived && (
              <Paper mt="md" p="xs" radius="md" bg="rgba(0,0,0,0.3)" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                <Group justify="space-between" wrap="nowrap">
                  <Group gap="xs">
                    <ThemeIcon size="sm" variant="transparent" color="gray.4">
                      <IconCalendarEvent size={16} />
                    </ThemeIcon>
                    <Text size="xs" c="gray.4" fw={600}>
                      Arşivlenme: {formatArchiveDate(vehicle.archived_at)}
                    </Text>
                  </Group>
                  <Badge color={getReasonColor(vehicle.archive_reason)} variant="filled" size="sm" radius="sm">
                    {vehicle.archive_reason || 'Diğer'}
                  </Badge>
                </Group>
              </Paper>
            )}
          </Paper>

          <Tabs
            defaultValue="general"
            color={vehicle.is_archived ? "gray" : "blue"}
            radius="xl"
            styles={{
              list: { gap: 8, flexWrap: 'wrap' },
              tab: { borderRadius: 999, fontWeight: 700 },
            }}
          >
            <Tabs.List>
              <Tabs.Tab value="general" leftSection={<IconCar size={16} />}>Genel Bilgiler</Tabs.Tab>
              <Tabs.Tab value="legal" leftSection={<IconFileCertificate size={16} />}>Yasal, Sigorta & Sistemler</Tabs.Tab>
              <Tabs.Tab value="history" leftSection={<IconHistory size={16} />}>Bakım Geçmişi</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="general" pt="md">
              <Stack gap="lg">
                <Paper withBorder radius="xl" p="lg" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}>
                  <Divider label={<Group gap={6}><IconCar size={14} /><span>Araç Kimliği</span></Group>} labelPosition="left" mb="md" />
                  <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
                    <InfoRow label="Marka" value={vehicle.brand} />
                    <InfoRow label="Model / Tip" value={vehicle.commercial_type} />
                    <InfoRow label="Model Yılı" value={vehicle.model_year} />
                    <InfoRow label="Araç Cinsi" value={vehicle.vehicle_type} />
                    <InfoRow label="Yakıt Türü" value={vehicle.fuel_type} />
                    <InfoRow label="Tescil Tarihi" value={formatDate(vehicle.registration_date)} />
                    <InfoRow label="Belge No" value={vehicle.document_no} />
                  </SimpleGrid>
                </Paper>

                <Paper withBorder radius="xl" p="lg" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}>
                  <Divider label={<Group gap={6}><IconAlertCircle size={14} /><span>Operasyonel Durum</span></Group>} labelPosition="left" mb="md" />
                  <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
                    <InfoRow label="Güncel Kilometre" value={vehicle.current_km ? `${vehicle.current_km.toLocaleString()} km` : '-'} />
                    <InfoRow label="Bulunduğu Lokasyon" value={vehicle.current_location} />
                    <InfoRow label="Tahsis Edilen Kişi" value={vehicle.allocated_to} />
                    <InfoRow label="Tahsis Edilen Kişi Tel No:" value={vehicle.phone} />
                  </SimpleGrid>
                </Paper>

                {vehicle.details?.explanation && (
                  <Blockquote color="blue" icon={<IconInfoCircle size={22} />} radius="xl" style={{ background: 'var(--mantine-color-blue-0)', border: '1px solid var(--mantine-color-blue-2)' }}>
                    <Text size="sm" fw={700} mb={4}>Araç Notları</Text>
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{vehicle.details.explanation}</Text>
                  </Blockquote>
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="legal" pt="md">
              <Stack gap="lg">
                <Paper withBorder radius="xl" p="lg" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}>
                  <Divider label={<Group gap={6}><IconDeviceDesktopAnalytics size={14} /><span>Araç İçi Sistemler & Yasal Belgeler</span></Group>} labelPosition="left" mb="md" />
                  <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                    <StatusBadge label="HGS" active={vehicle.details?.has_hgs} detail={vehicle.details?.hgs_no} />
                    <StatusBadge label="OTOBİL" active={vehicle.details?.has_otobil} detail={vehicle.details?.otobil_no} />
                    <StatusBadge label="Araç Takip Sistemi" active={vehicle.details?.has_vehicle_tracking} />
                    <StatusBadge label="K Belgesi" active={vehicle.details?.has_k_document} />
                    <StatusBadge label="D Belgesi" active={vehicle.details?.has_d_document} />
                    <StatusBadge label="UTTS" active={vehicle.details?.has_uets} />
                  </SimpleGrid>
                </Paper>

                <Paper withBorder radius="xl" p="lg" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}>
                  <Divider label={<Group gap={6}><IconFileCertificate size={14} /><span>Muayene ve Sigorta Bilgileri</span></Group>} labelPosition="left" mb="md" />
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg" mb="lg">
                    <Paper withBorder p="md" radius="xl" bg="orange.0" style={{ borderColor: 'var(--mantine-color-orange-2)' }}>
                      <Group mb="sm">
                        <ThemeIcon variant="light" color="orange" radius="xl"><IconFileCertificate size={18} /></ThemeIcon>
                        <Text fw={800} c="orange.9">Muayene Durumu</Text>
                      </Group>
                      <InfoRow label="Bitiş Tarihi" value={formatDate(vehicle.inspection_expiry_date)} />
                    </Paper>
                    <Paper withBorder p="md" radius="xl" bg="gray.0">
                      <Group mb="sm">
                        <ThemeIcon variant="light" color="gray" radius="xl"><IconGasStation size={18} /></ThemeIcon>
                        <Text fw={800}>Egzoz Emisyon</Text>
                      </Group>
                      <InfoRow label="Bitiş Tarihi" value={formatDate(vehicle.exhaust_emissions_date)} />
                    </Paper>
                  </SimpleGrid>
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
                    <Paper withBorder p="md" radius="xl">
                      <Text fw={800} mb="sm" c="blue">Trafik Sigortası</Text>
                      <Stack gap="sm">
                        <InfoRow label="Firma" value={vehicle.traffic_insurance_company} />
                        <InfoRow label="Poliçe No" value={vehicle.traffic_insurance_policy_no} />
                        <InfoRow label="Bitiş Tarihi" value={formatDate(vehicle.traffic_insurance_expiry)} />
                      </Stack>
                    </Paper>
                    <Paper withBorder p="md" radius="xl">
                      <Text fw={800} mb="sm" c="violet">Kasko</Text>
                      <Stack gap="sm">
                        <InfoRow label="Firma" value={vehicle.casco_company} />
                        <InfoRow label="Poliçe No" value={vehicle.casco_policy_no} />
                        <InfoRow label="Bitiş Tarihi" value={formatDate(vehicle.casco_expiry)} />
                      </Stack>
                    </Paper>
                  </SimpleGrid>
                </Paper>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="history" pt="md" style={{ minHeight: '320px', position: 'relative' }}>
              <LoadingOverlay visible={loadingHistory} overlayProps={{ blur: 1 }} />
              <ScrollArea h={420} type="auto" offsetScrollbars>
                {history.length > 0 ? (
                  <Timeline active={-1} bulletSize={26} lineWidth={2} mt="md" ml="md" mb="xl">
                    {history.map((item) => {
                      const style = getTimelineStyle(item.maintenance_type);
                      return (
                        <Timeline.Item key={item.id} bullet={style.icon} color={style.color} title={
                          <Group justify="space-between" align="center" mb={4}>
                            <Text size="md" fw={800}>{item.maintenance_type}</Text>
                            <Badge variant="light" color="gray" radius="xl">{formatDate(item.date)}</Badge>
                          </Group>
                        }>
                          <Stack gap={6} mt="xs">
                            {item.provider && (
                              <Group gap={6}>
                                <IconBuildingStore size={16} style={{ opacity: 0.7 }} />
                                <Text size="sm" fw={700}>{item.provider}</Text>
                              </Group>
                            )}
                            <Text c="dimmed" size="sm">
                              {item.km_at_maintenance ? `${item.km_at_maintenance.toLocaleString()} km` : '-'} • {formatCurrency(item.cost)}
                            </Text>
                            {item.description && (
                              <Paper withBorder p="sm" bg="gray.0" mt="xs" radius="lg">
                                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{item.description}</Text>
                              </Paper>
                            )}
                          </Stack>
                        </Timeline.Item>
                      );
                    })}
                  </Timeline>
                ) : (
                  !loadingHistory && (
                    <Stack align="center" justify="center" h={260} gap="sm">
                      <ThemeIcon size={70} radius="xl" variant="light" color="gray"><IconHistory size={34} /></ThemeIcon>
                      <Text c="dimmed" size="md" fw={600}>Bu araca ait henüz bir bakım kaydı bulunmuyor.</Text>
                    </Stack>
                  )
                )}
              </ScrollArea>
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Modal>

      <Modal
        opened={archiveModalOpened}
        onClose={closeArchiveModal}
        title={
          <Group gap="sm">
            <ThemeIcon color="orange" variant="light" radius="xl" size="md">
              <IconArchive size={18} />
            </ThemeIcon>
            <Text fw={800} size="lg">Arşive Kaldır</Text>
          </Group>
        }
        centered
        radius="xl"
        overlayProps={{ blur: 3, zIndex: 300 }}
        zIndex={301}
      >
        <Stack gap="lg">
          <Paper p="md" radius="md" bg="orange.0" style={{ border: '1px solid var(--mantine-color-orange-2)' }}>
            <Text size="sm" c="dark.8">
              <b>{vehicle?.plate}</b> plakalı aracı aktif filodan çıkarıp arşive kaldırmak üzeresiniz. 
              Lütfen işlemi tamamlamak için aşağıdaki listeden arşivleme sebebini seçin.
            </Text>
          </Paper>

          <Select
            label="Arşivleme Sebebi"
            placeholder="Bir sebep seçin"
            data={['Satıldı', 'Hurdaya Ayrıldı', 'Kiralama Süresi Bitti', 'Sözleşme Feshi', 'Diğer']}
            value={archiveReason}
            onChange={(val) => {
              setArchiveReason(val);
              if (val !== 'Diğer') setCustomReason(''); 
            }}
            allowDeselect={false}
            radius="md"
            withAsterisk
            comboboxProps={{ 
              withinPortal: true, 
              zIndex: 400,
              position: 'bottom'
            }}
          />

          {archiveReason === 'Diğer' && (
            <TextInput
              label="Lütfen Sebebi Belirtin"
              placeholder="Arşivleme sebebini kısaca yazın..."
              value={customReason}
              onChange={(e) => setCustomReason(e.currentTarget.value)}
              radius="md"
              withAsterisk
              data-autofocus 
            />
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="default" radius="xl" onClick={closeArchiveModal} disabled={isArchiving}>
              İptal
            </Button>
            <Button 
              color="orange" 
              radius="xl" 
              onClick={handleArchiveVehicle} 
              loading={isArchiving}
            >
              Kaydet ve Arşivle
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}